// useCountryData — country scorecard data for the Analysis panel (US-879 ·
// R-3/R-4/R-8).
//
// The one point where the panel meets the data layer: components render
// { data, loading, error } and never fetch (docs/FRONTEND_DATA_LAYER.md §4).
// Data comes through the useApi seam from the catalog's countryDataRange
// route — today's reference contract for country data (202 + scorecard,
// sdd/specs/US-891/contract.md). If EPIC-16 ships a dedicated country-data
// endpoint, this hook is the only place to re-point (R-4).
//
// The 202 body also carries the async report pointers (task_id/status_url);
// this hook returns the body intact and the panel simply ignores them —
// polling is US-882's useReportJob. `cache: {}` opts into the client cache:
// the scorecard is a deterministic simulation (the backend caches it
// server-side under the same premise), so revisiting a country within the
// TTL costs no request.
import { useEffect, useState } from "react";
import useApi from "./useApi";
import endpoints from "../api/endpoints";
import { countryRequestBody } from "../config/simulationDefaults";
// Side effect: registers the countryDataRange contract fixture, so with
// REACT_APP_USE_FIXTURES on the same call below resolves without a network
// (static demo / backend-less dev — US-883 mechanism).
import "../fixtures/countryData";

const IDLE = { data: null, error: null, loading: false };

// Fetch the scorecard for one country, keyed by ISO3 (e.g. "PHL"). A falsy
// iso3 (no selection) is the idle state, not an error. Re-fires on country
// change, aborting the in-flight request; AbortError is silence, not error
// (R-3 — useApi re-throws it unwrapped).
const useCountryData = (iso3) => {
  const api = useApi();
  const [state, setState] = useState(IDLE);

  useEffect(() => {
    if (!iso3) {
      setState(IDLE);
      return undefined;
    }
    const controller = new AbortController();
    setState({ data: null, error: null, loading: true });
    const fetchScorecard = () =>
      api.post(endpoints.countryDataRange.path, countryRequestBody(iso3), {
        signal: controller.signal,
        cache: {},
      });
    fetchScorecard()
      .catch((error) => {
        // A foreign AbortError (ours never fires with a live signal): the
        // client cache deduped us onto an in-flight request that another —
        // already unmounted — instance started and aborted (StrictMode
        // double-mounts with a country pre-selected). The cache has already
        // dropped that entry by the time this handler runs (its cleanup was
        // attached first), so one retry re-executes on OUR signal.
        if (error.name === "AbortError" && !controller.signal.aborted) {
          return fetchScorecard();
        }
        throw error;
      })
      .then((data) => setState({ data, error: null, loading: false }))
      .catch((error) => {
        if (error.name === "AbortError") return; // unmount/re-fire: no setState
        setState({ data: null, error, loading: false }); // error is an ApiError
      });
    return () => controller.abort();
  }, [api, iso3]);

  return state;
};

export default useCountryData;
